import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  Users, BarChart3, Plus, Loader2, Building2, Receipt, TrendingUp, RefreshCw,
} from "lucide-react";

export default function ChannelDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [subPartners, setSubPartners] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", password: "" });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dashRes, spRes, txnRes, earnRes] = await Promise.all([
        api.get("/channel/dashboard"),
        api.get("/channel/sub-partners"),
        api.get("/channel/transactions?limit=50"),
        api.get("/channel/earnings"),
      ]);
      setDashboard(dashRes.data);
      setSubPartners(spRes.data.sub_partners || []);
      setTransactions(txnRes.data.transactions || []);
      setEarnings(earnRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load channel data");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const onCreateSubPartner = async () => {
    if (!form.full_name || !form.email || !form.mobile || !form.password) {
      toast.error("All fields are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/channel/sub-partners", form);
      toast.success("Sub-partner created successfully");
      setCreateOpen(false);
      setForm({ full_name: "", email: "", mobile: "", password: "" });
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create sub-partner");
    } finally { setBusy(false); }
  };

  const ch = dashboard?.channel;
  const stats = dashboard?.stats;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading channel…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="animate-fade-up" data-testid="channel-dashboard">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Channel Partner</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2 flex items-center gap-2">
              <Building2 className="h-7 w-7 text-primary" strokeWidth={1.5} />
              {ch?.name || "My Channel"}
              <Badge variant="outline" className="ml-2 font-mono text-xs">{ch?.code}</Badge>
            </h1>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="add-sub-partner-btn">
            <Plus className="h-4 w-4 mr-1" /> Add sub-partner
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="surface-card p-5" data-testid="kpi-sub-partners">
            <Users className="h-5 w-5 text-primary mb-2" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sub-partners</div>
            <div className="font-display text-3xl font-bold mt-1">{stats?.sub_partner_count || 0}</div>
          </div>
          <div className="surface-card p-5" data-testid="kpi-txn-count">
            <Receipt className="h-5 w-5 text-primary mb-2" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Transactions</div>
            <div className="font-display text-3xl font-bold mt-1">{stats?.txn_count || 0}</div>
          </div>
          <div className="surface-card p-5" data-testid="kpi-volume">
            <BarChart3 className="h-5 w-5 text-primary mb-2" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total volume</div>
            <div className="font-display text-3xl font-bold mt-1">{formatINR(stats?.total_volume || 0)}</div>
          </div>
          <div className="surface-card p-5" data-testid="kpi-commission">
            <TrendingUp className="h-5 w-5 text-secondary mb-2" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. commission</div>
            <div className="font-display text-3xl font-bold mt-1 text-secondary">{formatINR(earnings?.totals?.estimated_commission || 0)}</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="partners" className="mt-8" data-testid="channel-tabs">
          <TabsList>
            <TabsTrigger value="partners" data-testid="tab-sub-partners">Sub-partners ({subPartners.length})</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions ({transactions.length})</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
          </TabsList>

          {/* Sub-partners tab */}
          <TabsContent value="partners" className="mt-4">
            <div className="surface-card overflow-hidden">
              {subPartners.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-muted-foreground">No sub-partners yet. Add your first one.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="p-3 pl-5">Name</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Mobile</th>
                      <th className="p-3">KYC</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right pr-5">Wallet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subPartners.map((sp) => (
                      <tr key={sp.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`sub-partner-${sp.id}`}>
                        <td className="p-3 pl-5 font-medium">{sp.full_name}</td>
                        <td className="p-3 text-xs">{sp.email}</td>
                        <td className="p-3 font-mono text-xs">{sp.mobile}</td>
                        <td className="p-3"><Badge variant={sp.kyc_status === "verified" ? "default" : "outline"} className={sp.kyc_status === "verified" ? "bg-success text-success-foreground" : ""}>{sp.kyc_status}</Badge></td>
                        <td className="p-3"><Badge variant="outline">{sp.account_status}</Badge></td>
                        <td className="p-3 text-right pr-5 font-mono">{formatINR(sp.wallet_balance || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Transactions tab */}
          <TabsContent value="transactions" className="mt-4">
            <div className="surface-card overflow-hidden">
              {transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <Receipt className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-muted-foreground">No transactions yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="p-3 pl-5">Txn ID</th>
                        <th className="p-3">Partner</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Category</th>
                        <th className="p-3 text-right">Amount</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t) => (
                        <tr key={t.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                          <td className="p-3 pl-5 font-mono text-xs">{t.display_id}</td>
                          <td className="p-3">{t.partner?.full_name || "—"}</td>
                          <td className="p-3"><Badge variant="outline">{t.type}</Badge></td>
                          <td className="p-3 text-xs">{t.category || "—"}</td>
                          <td className="p-3 text-right font-mono">{formatINR(t.amount)}</td>
                          <td className="p-3"><Badge variant={t.status === "success" ? "default" : "outline"} className={t.status === "success" ? "bg-success text-success-foreground" : ""}>{t.status}</Badge></td>
                          <td className="p-3 text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Earnings tab */}
          <TabsContent value="earnings" className="mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="surface-card p-6">
                <h3 className="font-semibold text-lg mb-4">Commission summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-muted-foreground">Total volume</span><span className="font-mono font-semibold">{formatINR(earnings?.totals?.volume || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total transactions</span><span className="font-mono font-semibold">{earnings?.totals?.txn_count || 0}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Commission rate</span><span className="font-mono font-semibold">{earnings?.totals?.commission_rate || 0}%</span></div>
                  <div className="border-t border-border pt-3 flex justify-between text-lg">
                    <span className="font-semibold">Estimated commission</span>
                    <span className="font-mono font-bold text-secondary">{formatINR(earnings?.totals?.estimated_commission || 0)}</span>
                  </div>
                </div>
              </div>
              <div className="surface-card p-6">
                <h3 className="font-semibold text-lg mb-4">By transaction type</h3>
                {(earnings?.by_type || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-2">
                    {(earnings?.by_type || []).map((bt) => (
                      <div key={bt.type} className="flex items-center justify-between px-3 py-2 rounded border border-border/50">
                        <div>
                          <Badge variant="outline">{bt.type}</Badge>
                          <span className="ml-2 text-xs text-muted-foreground">{bt.count} txns</span>
                        </div>
                        <span className="font-mono font-semibold">{formatINR(bt.volume)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create sub-partner dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add sub-partner</DialogTitle>
            <DialogDescription>Create a new partner account under your channel. They can login and process transactions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Partner name" data-testid="sp-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="partner@email.com" data-testid="sp-email" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876543210" data-testid="sp-mobile" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" data-testid="sp-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={onCreateSubPartner} disabled={busy} data-testid="create-sp-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create sub-partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
